<script lang="ts" setup>
import { computed } from 'vue';
import { BannerDecor } from '~/components';
import { AppTypography } from '~/components/base';
import type { Candidate } from '~/types/Candidates';
import { $pluralizeWithCount } from '~/utils/pluralize';

const { candidate, partyHref = null } = defineProps<{
  candidate: Candidate;
  /** Link to the candidate's party page; omit/null hides the "View Party" button. */
  partyHref?: string | null;
}>();

const statusLabel = computed(() => {
  if (candidate.incumbent) return 'Incumbent';
  if (!candidate.party) return 'Independent';
  return null;
});
</script>

<template>
  <div data-testid="candidate-profile-card" class="profile-card">
    <div class="profile-card__band">
      <BannerDecor />
    </div>

    <span
      v-if="statusLabel"
      class="profile-card__chip inline-flex w-fit items-center"
      data-testid="candidate-profile-status"
    >
      <AppTypography type="caption-bold" element="span" class="uppercase">{{
        statusLabel
      }}</AppTypography>
    </span>

    <span
      class="profile-card__avatar flex items-center justify-center"
      data-testid="candidate-profile-avatar"
    >
      <img
        v-if="candidate.image"
        :src="candidate.image"
        :alt="candidate.name"
        width="112"
        height="112"
        decoding="async"
        class="profile-card__avatar-image size-full object-cover"
      />
      <!-- Figma: no-photo candidates show a silhouette avatar here, not
           initials (VTW-558) — initials remain the fallback on the smaller
           list card (CandidateCard.vue), which has no room for the icon. -->
      <Icon
        v-else
        name="solar:user-rounded-bold"
        class="profile-card__avatar-fallback-icon"
      />
    </span>

    <div class="profile-card__body flex flex-col items-center text-center">
      <AppTypography type="title-5" element="p" class="mt-3">
        {{ candidate.name }}
      </AppTypography>

      <div class="profile-card__stats mt-3 flex items-center gap-3">
        <span class="flex items-center gap-1.5">
          <Icon name="solar:notes-linear" size="24" class="shrink-0" />
          <AppTypography type="body">
            {{ $pluralizeWithCount(candidate.campaignsCount, 'Campaign') }}
          </AppTypography>
        </span>
        <span class="profile-card__divider shrink-0" />
        <span class="flex items-center gap-1.5">
          <Icon name="solar:buildings-linear" size="24" class="shrink-0" />
          <AppTypography type="body">
            {{ $pluralizeWithCount(candidate.lobbyistsCount, 'Lobbyist') }}
          </AppTypography>
        </span>
      </div>

      <NuxtLink
        v-if="partyHref && candidate.party"
        :to="partyHref"
        data-testid="view-party-button"
        class="profile-card__party-button mt-4 flex w-full items-center justify-center gap-2 text-center no-underline"
      >
        <Icon name="solar:users-group-two-rounded-linear" class="shrink-0" />
        <AppTypography type="body-bold" element="span" class="truncate"
          >View {{ candidate.party }}</AppTypography
        >
      </NuxtLink>
    </div>
  </div>
</template>

<style scoped lang="scss">
.profile-card {
  position: relative;
  overflow: hidden;
  width: 100%;
  // Figma: borders/default (#e8e3db) — this app's $border-color /
  // $border-color-secondary are a different (navy) palette.
  border: 1px solid #e8e3db;
  border-radius: $radius-lg;
  background-color: $surface-color-bg;

  @media (min-width: 1024px) {
    width: 360px;
    max-width: 360px;
    // Desktop lays this out as a fixed sidebar beside the tab panel. Without
    // this the card is shrinkable (width: 100% + overflow: hidden), so a
    // sibling panel whose content refuses to narrow can squeeze it to nothing.
    flex-shrink: 0;
  }

  &__band {
    position: relative;
    height: 85px;
    background-color: $surface-color-brand;
    overflow: hidden;
  }

  &__body {
    // The avatar is absolutely positioned overlapping the band by half its
    // height (56px), plus the status chip that hangs off the avatar's bottom
    // edge; reserve space here so the name/etc. clear both (chip bottom is
    // 153px; band 85px + this padding = 159px, with the name's own mt-3
    // covering the rest).
    padding: 74px $spacing-xl $spacing-xl;
  }

  &__avatar {
    position: absolute;
    top: 29px; // band height (85px) minus half the avatar height (56px)
    left: 50%;
    z-index: 1;
    width: 112px;
    height: 112px;
    overflow: hidden;
    border: 4px solid $complementary-color;
    border-radius: 9999px;
    background-color: $surface-color-bg;
    color: $text-color-primary;
    transform: translateX(-50%);
  }

  &__avatar-image {
    border-radius: 9999px;
  }

  &__avatar-fallback-icon {
    width: 56px;
    height: 56px;
  }

  // Figma: the status chip hangs off the bottom of the avatar — vertically
  // centred on the avatar's bottom edge (29px top + 112px tall = 141px) and
  // layered above it, sitting between the avatar and the name.
  &__chip {
    position: absolute;
    top: 129px; // 141px avatar bottom, minus half the 24px chip height
    left: 50%;
    z-index: 2; // above the avatar (z-index: 1)
    padding: 4px $spacing-md;
    border-radius: $radius-2xl;
    background-color: $complementary-color-shade-1;
    color: $text-color-brand;
    transform: translateX(-50%);
  }

  &__stats {
    color: $text-color-secondary;
  }

  &__divider {
    width: 1px;
    height: 16px;
    background-color: #e8e3db;
  }

  &__party-button {
    padding: $spacing-md $spacing-lg;
    border: 1px solid $border-color-brand;
    border-radius: 9999px;
    color: $text-color-primary;
    transition: background-color 0.15s ease;

    // Neutralize the global .router-link-active background so it doesn't
    // paint over this outlined button.
    &.router-link-active {
      background-color: transparent;
    }

    &:hover {
      background-color: $surface-color-bg-2;
    }
  }
}
</style>
